import { createFileRoute, Navigate, Outlet, useMatch } from "@tanstack/react-router";
import { HTTPError } from "ky";
import { useBusiness } from "@/api/hooks";
import { ConsumerLayout } from "@/components/layout/consumer-layout";
import { EmptyState } from "@/components/ui/empty-state";
import { Skeleton } from "@/components/ui/skeleton";
import { BusinessPage } from "@/features/business/business-page";
import { SearchBar } from "@/features/search/search-bar/search-bar";

export const Route = createFileRoute("/businesses/$slug")({
  component: BusinessLayout,
});

function BusinessLayout() {
  const { slug } = Route.useParams();
  const bookMatch = useMatch({ from: "/businesses/$slug/book", shouldThrow: false });
  const query = useBusiness(slug);

  if (query.data && query.data.slug !== slug) {
    return (
      <Navigate
        to={bookMatch ? "/businesses/$slug/book" : "/businesses/$slug"}
        params={{ slug: query.data.slug }}
        search={(previous) => previous}
        hash
        replace
      />
    );
  }

  if (bookMatch) {
    return <Outlet />;
  }

  return (
    <ConsumerLayout headerContent={<SearchBar mode="compact" />} compactNav>
      <BusinessDetailContent query={query} />
    </ConsumerLayout>
  );
}

// fallow-ignore-next-line complexity
function BusinessDetailContent({ query }: { query: ReturnType<typeof useBusiness> }) {
  const { data, isLoading, error } = query;

  if (isLoading) {
    return <BusinessSkeleton />;
  }

  if (error) {
    const is404 = error instanceof HTTPError && error.response.status === 404;
    if (is404) {
      return (
        <div className="py-20">
          <EmptyState
            pose="search"
            title="Business not found"
            description="The business you're looking for doesn't exist or may have been removed."
          />
        </div>
      );
    }
    return (
      <div className="py-20">
        <EmptyState
          pose="shrug"
          title="Something went wrong"
          description="We couldn't load this business. Please try again later."
        />
      </div>
    );
  }

  if (!data) return null;

  return <BusinessPage business={data} />;
}

function BusinessSkeleton() {
  return (
    <div className="mx-auto max-w-5xl px-4 py-6 sm:px-6 lg:px-8">
      <Skeleton variant="card" className="aspect-[21/9] h-auto rounded-lg" />
      <div className="mt-6 flex flex-col gap-3">
        <Skeleton variant="text" className="h-10 w-2/3" />
        <div className="flex gap-2">
          <Skeleton variant="text" className="h-6 w-24" />
          <Skeleton variant="text" className="h-6 w-20" />
        </div>
        <Skeleton variant="text" className="h-5 w-1/2" />
      </div>
      <div className="mt-8 flex flex-col gap-4">
        <div className="flex gap-4 border-b border-fog pb-3">
          <Skeleton variant="text" className="h-5 w-20" />
          <Skeleton variant="text" className="h-5 w-20" />
          <Skeleton variant="text" className="h-5 w-20" />
        </div>
        <Skeleton variant="card" className="h-24 rounded-md" />
        <Skeleton variant="card" className="h-24 rounded-md" />
        <Skeleton variant="card" className="h-24 rounded-md" />
      </div>
    </div>
  );
}
